/**
 * A strict template's days, read as weekdays. Day zero of the cycle is the first day of the
 * week — which day that is belongs to the reader's locale, not the template — and a cycle
 * longer than a week runs on into week two and onward.
 *
 * Weekdays are numbered the way `Intl` numbers them, 1 for Monday through 7 for Sunday, which
 * is also how a run's anchor is kept.
 */

import { startOfDay } from '~/lib/dates'

/** The first day of the week where this device is: 1 for Monday through 7 for Sunday. */
export function firstDayOfWeek(): number {
  try {
    const locale = new Intl.Locale(navigator.language) as Intl.Locale & {
      weekInfo?: { firstDay: number }
      getWeekInfo?: () => { firstDay: number }
    }
    return locale.weekInfo?.firstDay ?? locale.getWeekInfo?.().firstDay ?? 1
  } catch {
    return 1
  }
}

/** The name a day of the cycle wears: its weekday, counted from the week's local start. */
export function weekdayName(day: number): string {
  // The first week of January 2024 starts on a Monday, so ISO day n is January n.
  const date = new Date(2024, 0, ((firstDayOfWeek() + (day % 7) - 1) % 7) + 1)
  try {
    return new Intl.DateTimeFormat(navigator.language, { weekday: 'short' }).format(date)
  } catch {
    return date.toDateString().slice(0, 3)
  }
}

/** All seven weekday names, in the order this locale's week runs. */
export function weekdayNames(): string[] {
  return Array.from({ length: 7 }, (_, day) => weekdayName(day))
}

/**
 * Which column of a week the cycle's first day sits in, once the anchor has landed it. Zero
 * when the cycle begins on the week's own first day, which is how the editor draws a cycle and
 * what an unanswered anchor would have meant.
 */
export function cycleStartColumn(anchor: number): number {
  return (anchor - firstDayOfWeek() + 7) % 7
}

/** What a weekday is called, given its number: 1 for Monday through 7 for Sunday. */
export function weekdayNameOf(weekday: number): string {
  return weekdayName((weekday - firstDayOfWeek() + 7) % 7)
}

/** The seven weekdays a run can be anchored to, in the order the week runs here. */
export function weekOrder(): { weekday: number; name: string }[] {
  return Array.from({ length: 7 }, (_, offset) => ({
    weekday: ((firstDayOfWeek() - 1 + offset) % 7) + 1,
    name: weekdayName(offset),
  }))
}

/** Which weekday a moment falls on, 1 for Monday through 7 for Sunday. */
export function weekdayOf(timestamp: number): number {
  const day = new Date(timestamp).getDay()
  return day === 0 ? 7 : day
}

/**
 * How far into a strict cycle a moment is, before the cycle's length is folded in. Day zero is
 * the run's anchor weekday rather than the moment somebody pressed start, so a program that
 * squats on the week's first day squats there whichever evening it was started — the count
 * runs from the last anchor weekday on or before that.
 *
 * Whole calendar days, not spans of 24 hours: a weekday that turned over at the hour somebody
 * enrolled would be no weekday at all. An unanchored run counts from the day it began.
 */
export function daysIntoCycle(startedAt: number, at: number, anchor: number | null): number {
  // Rounded, so a daylight-saving shift inside the span cannot lose or gain a day.
  const days = Math.round((startOfDay(new Date(at)) - startOfDay(new Date(startedAt))) / 86_400_000)
  return anchor === null ? days : days + ((weekdayOf(startedAt) - anchor + 7) % 7)
}
